/**
 * The deployment's own name, and its look.
 *
 * One frontend build ships in one wheel and serves every instance, so the
 * name cannot be baked in — and it was. `index.html`, the sidebar wordmark
 * and the login card all said `orchd`, which on the `wingd` deployment
 * meant a public login page naming a different instance.
 *
 * The look had the same problem and is fixed the same way: every instance
 * wore orchd's mark and palette. `/instance` now carries the brand from
 * `web.brand` in the instance's configuration.
 *
 * Fetched rather than imported, and from an **unauthenticated** endpoint:
 * the login page needs both before anyone has logged in.
 */

/** Until the fetch lands, and if it never does. */
export const FALLBACK_INSTANCE = 'machinai';

export type Tone = 'primary' | 'accent' | 'dim';
export type Mark = 'conductor' | 'wing';

export interface Brand {
  accent: string;
  accentSecondary: string;
  background: string;
  mark: Mark;
  /** `[text, tone]` segments. Empty means "the instance name, plainly". */
  wordmark: Array<[string, Tone]>;
  tagline: string;
  /** The tab title. `null` means the instance name. */
  title: string | null;
}

export interface InstanceInfo {
  name: string;
  brand: Brand;
}

/**
 * Today's look — orchd's — which is what an unbranded instance keeps. The
 * same defaults as `panel/server/brand.py`.
 */
export const DEFAULT_BRAND: Brand = {
  accent: '#6366f1',
  accentSecondary: '#8b5cf6',
  background: '#07070f',
  mark: 'conductor',
  wordmark: [],
  tagline: 'personal agent',
  title: null,
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const TONES: readonly string[] = ['primary', 'accent', 'dim'];
const MARKS: readonly string[] = ['conductor', 'wing'];

const colour = (v: unknown, fallback: string): string =>
  typeof v === 'string' && HEX.test(v) ? v : fallback;

/**
 * The brand, checked again here although the server validates it.
 *
 * These values are written into `style` — CSS custom properties that the
 * whole stylesheet reads — so the page does not take a response's word for
 * them. A value that fails is the default, field by field, as on the server.
 */
function brandOf(raw: unknown): Brand {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_BRAND;
  const r = raw as Record<string, unknown>;
  const accent = colour(r.accent, DEFAULT_BRAND.accent);
  const wordmark: Array<[string, Tone]> = [];
  if (Array.isArray(r.wordmark)) {
    for (const seg of r.wordmark) {
      if (
        Array.isArray(seg) &&
        seg.length === 2 &&
        typeof seg[0] === 'string' &&
        seg[0] !== '' &&
        typeof seg[1] === 'string' &&
        TONES.includes(seg[1])
      ) {
        wordmark.push([seg[0], seg[1] as Tone]);
      } else {
        // All or nothing, as on the server: a dropped segment spells a
        // different word.
        wordmark.length = 0;
        break;
      }
    }
  }
  return {
    accent,
    accentSecondary: colour(r.accent_secondary, accent),
    background: colour(r.background, DEFAULT_BRAND.background),
    mark: typeof r.mark === 'string' && MARKS.includes(r.mark) ? (r.mark as Mark) : 'conductor',
    wordmark,
    tagline: typeof r.tagline === 'string' && r.tagline ? r.tagline : DEFAULT_BRAND.tagline,
    title: typeof r.title === 'string' && r.title ? r.title : null,
  };
}

export const fetchInstance = async (): Promise<InstanceInfo> => {
  try {
    // Not `apiFetch`: that one is for `/api/*` and carries auth handling
    // this endpoint neither needs nor has.
    const res = await fetch('/instance', { credentials: 'same-origin' });
    if (!res.ok) return { name: FALLBACK_INSTANCE, brand: DEFAULT_BRAND };
    const body: unknown = await res.json();
    const b = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
    const name = typeof b.instance === 'string' && b.instance.trim() ? b.instance.trim() : FALLBACK_INSTANCE;
    return { name, brand: brandOf(b.brand) };
  } catch {
    // A panel that cannot name itself must still render. Generic-and-wrong
    // beats specific-and-wrong, which is the bug this replaces.
    return { name: FALLBACK_INSTANCE, brand: DEFAULT_BRAND };
  }
};

/** `"#6d7bef"` → `"109, 123, 239"`, for `rgba(var(--…-rgb), a)`. */
export function rgbTriple(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}`;
}
